/**
 * LeagueSchedulePage — /league/:leagueId/schedule
 * ─────────────────────────────────────────────────────────────────
 * Phase 4. Season-scoped schedule management:
 *   • Generator wizard (constraints → preview → commit)
 *   • Schedule list / grid view of all games in the season
 *   • Inline edit + cancel per game
 *
 * Permissions (mirror backend):
 *   • Viewing schedule: any league member.
 *   • Generate / commit / edit / cancel: manage_schedule.
 *
 * UI structure:
 *   • Season picker (default: active, else most recent).
 *   • Top toolbar with "Generate / regenerate" button.
 *   • Below: list of games grouped by month-week.
 *   • Per-game row: date + time + venue + matchup + status badge
 *     + counter "0/4 officials assigned".
 *   • Click a row to expand → inline edit form + cancel button.
 *
 * 2026-09-28 — List / Calendar toggle (month grid; click a game for its
 * sheet), ?season=<id> deep links, and a fix for a hooks-order crash
 * (useMemo sat after an early return in SeasonScheduleSection, so the
 * first render after loading threw "Rendered more hooks…").
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import {
  Calendar, Sparkles, Plus, X, Save, Trash2,
  AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2,
  MapPin, Clock, Shield, ChevronDown, ChevronRight, RefreshCw, PenLine,
  List, CalendarDays, ChevronLeft,
} from 'lucide-react';
import api from '../../services/api.js';
import toast from 'react-hot-toast';
import LeagueSubNav from './LeagueSubNav.jsx';
import { useLeagueMembership } from '../../hooks/useLeagueMembership.js';
// v2.5.125 — Phase 11 date consistency.
import { formatDate } from '../../utils/formatDate.js';

export default function LeagueSchedulePage() {
  const { leagueId } = useParams();
  const { isManager, can, loading: roleLoading } = useLeagueMembership(leagueId);
  const canManage = isManager || can('manage_schedule');

  const [league, setLeague]   = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonIdState] = useState('');
  const [loading, setLoading] = useState(true);
  // 2026-09-28 — ?season=<id> deep link; the picker keeps it in sync.
  const [searchParams, setSearchParams] = useSearchParams();
  const setSeasonId = (id) => {
    setSeasonIdState(id);
    const next = new URLSearchParams(searchParams);
    if (id) next.set('season', id); else next.delete('season');
    setSearchParams(next, { replace: true });
  };

  const defaultSeasonId = useMemo(() => {
    if (seasons.length === 0) return '';
    const wanted = searchParams.get('season');
    if (wanted && seasons.some(s => s.id === wanted)) return wanted;
    const active = seasons.find(s => s.status === 'active');
    return active?.id || seasons[0].id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasons]);

  async function loadShell() {
    if (!leagueId) return;
    try {
      const [leagueRes, seasonsRes] = await Promise.all([
        api.get(`/leagues/${leagueId}`),
        api.get(`/leagues/${leagueId}/seasons`),
      ]);
      setLeague(leagueRes?.data?.league || null);
      setSeasons(seasonsRes?.data?.seasons || []);
    } catch (err) {
      if (err?.response?.status === 403) toast.error('You are not a member of this league');
      else if (err?.response?.status !== 401) toast.error('Could not load league');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadShell(); /* eslint-disable-next-line */ }, [leagueId]);

  useEffect(() => {
    if (!seasonId && defaultSeasonId) setSeasonIdState(defaultSeasonId);
  }, [defaultSeasonId, seasonId]);

  if (loading || roleLoading) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 1080 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading…</div>
      </div>
    );
  }
  if (!league) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 1080 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>
          League not found or no access.
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" style={{ maxWidth: 1080 }}>
      <LeagueSubNav league={league} />

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 className="section-title text-xl">Schedule</h2>
          <p style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4 }}>
            Generate a round-robin schedule for the season, or manage games one-by-one.
          </p>
        </div>
      </div>

      {seasons.length === 0 ? (
        <EmptyNoSeasonsCard leagueId={leagueId} />
      ) : (
        <>
          <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Calendar size={16} color="var(--ice)" />
            <span style={{ fontSize: 13, fontWeight: 600 }}>Season:</span>
            <select value={seasonId} onChange={(e) => setSeasonId(e.target.value)}
              style={{ ...inputStyle, width: 'auto', minWidth: 240, flex: '1 1 280px' }}>
              {seasons.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.status})
                </option>
              ))}
            </select>
          </div>

          {seasonId && (
            <SeasonScheduleSection
              leagueId={leagueId}
              seasonId={seasonId}
              canManage={canManage}
            />
          )}
        </>
      )}
    </div>
  );
}

function EmptyNoSeasonsCard({ leagueId }) {
  return (
    <div className="card" style={{ textAlign: 'center', padding: 36 }}>
      <Calendar size={36} color="var(--text-2)" style={{ margin: '0 auto 14px', opacity: 0.5 }} />
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>No seasons yet</div>
      <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 18 }}>
        Schedules are per-season. Create a season first.
      </div>
      <Link to={`/league/${leagueId}/seasons`} className="btn-ice"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <Plus size={14} />
        Go to Seasons
        <ArrowRight size={12} />
      </Link>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Season schedule section — games list + generator launch
// ═══════════════════════════════════════════════════════════════

const VIEW_KEY = 'gd.leagueSchedule.view';
function readView() {
  try { return localStorage.getItem(VIEW_KEY) === 'calendar' ? 'calendar' : 'list'; } catch { return 'list'; }
}

function SeasonScheduleSection({ leagueId, seasonId, canManage }) {
  const [games, setGames]   = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [showGenerator, setShowGenerator] = useState(false);
  const [view, setViewState] = useState(readView);
  const setView = (v) => {
    setViewState(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* private mode */ }
  };

  async function load() {
    setLoading(true);
    try {
      const [gRes, vRes] = await Promise.all([
        api.get(`/leagues/${leagueId}/seasons/${seasonId}/games`),
        api.get(`/leagues/${leagueId}/venues`).catch(() => ({ data: { venues: [] } })),
      ]);
      setGames(gRes.data?.games || []);
      setVenues(vRes.data?.venues || []);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error('Could not load games');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [seasonId]);

  // Group games by month (YYYY-MM) for the visual grouping. Must stay
  // ABOVE the loading early-return (hooks run in the same order on
  // every render).
  const grouped = useMemo(() => {
    const map = new Map();
    for (const g of games) {
      const ym = (g.game_date || '').slice(0, 7);
      if (!map.has(ym)) map.set(ym, []);
      map.get(ym).push(g);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [games]);

  if (loading) return <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading…</div>;

  const totalActive = games.filter(g => g.status !== 'cancelled').length;
  const totalCancelled = games.filter(g => g.status === 'cancelled').length;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
          {totalActive} game{totalActive === 1 ? '' : 's'} scheduled
          {totalCancelled > 0 && <span style={{ color: 'var(--text-3)' }}>{' '}· {totalCancelled} cancelled</span>}
        </span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {games.length > 0 && (
            <div role="group" aria-label="View" style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              {[['list', List, 'List'], ['calendar', CalendarDays, 'Calendar']].map(([id, Icon, label]) => (
                <button key={id} type="button" onClick={() => setView(id)} aria-pressed={view === id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', border: 'none', cursor: 'pointer',
                  background: view === id ? 'var(--ice-a10)' : 'transparent',
                  color: view === id ? 'var(--ice)' : 'var(--text-2)', fontWeight: 700, fontSize: 12,
                }}>
                  <Icon size={13} /> {label}
                </button>
              ))}
            </div>
          )}
          <button type="button" className="btn-ghost" onClick={load}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={12} />
            Refresh
          </button>
          {canManage && (
            <button type="button" className="btn-ice" onClick={() => setShowGenerator(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Sparkles size={13} />
              {games.length > 0 ? 'Regenerate' : 'Generate schedule'}
            </button>
          )}
        </div>
      </div>

      {games.length === 0 ? (
        <ScheduleEmpty canManage={canManage} onGenerate={() => setShowGenerator(true)} />
      ) : view === 'calendar' ? (
        <ScheduleCalendar games={games} leagueId={leagueId} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {grouped.map(([ym, list]) => (
            <div key={ym}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: 'var(--text-2)',
                textTransform: 'uppercase', letterSpacing: 0.6,
                marginBottom: 8,
              }}>
                {formatYearMonth(ym)} · {list.filter(g => g.status !== 'cancelled').length} game{list.filter(g => g.status !== 'cancelled').length === 1 ? '' : 's'}
              </div>
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {list.map((g, i) => (
                  <GameRow key={g.id} game={g} isLast={i === list.length - 1}
                    leagueId={leagueId} seasonId={seasonId}
                    venues={venues} canManage={canManage}
                    expanded={expandedId === g.id}
                    onToggle={() => setExpandedId(expandedId === g.id ? null : g.id)}
                    onChanged={load} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showGenerator && (
        <GeneratorWizard
          leagueId={leagueId}
          seasonId={seasonId}
          hasExistingGames={totalActive > 0}
          onClose={() => setShowGenerator(false)}
          onDone={() => { setShowGenerator(false); load(); }} />
      )}
    </>
  );
}

// ─── Calendar view (2026-09-28) ─────────────────────────────────
// Month grid, Sunday-first. Each game is a chip that opens its game
// sheet; locked games show the final score. Opens on the month of the
// next game still to play (or the last month with games).
function ScheduleCalendar({ games, leagueId }) {
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const months = useMemo(() => {
    const set = new Set(games.map(g => (g.game_date || '').slice(0, 7)).filter(Boolean));
    return Array.from(set).sort();
  }, [games]);
  const initial = useMemo(() => {
    const next = games.find(g => g.status !== 'cancelled' && (g.game_date || '') >= todayStr);
    return (next?.game_date || games[games.length - 1]?.game_date || todayStr).slice(0, 7);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [games]);
  const [ym, setYm] = useState(initial);
  useEffect(() => { setYm(initial); }, [initial]);

  const byDate = useMemo(() => {
    const m = new Map();
    for (const g of games) {
      if (!m.has(g.game_date)) m.set(g.game_date, []);
      m.get(g.game_date).push(g);
    }
    for (const list of m.values()) list.sort((a, b) => String(a.game_time || '').localeCompare(String(b.game_time || '')));
    return m;
  }, [games]);

  const [y, mo] = ym.split('-').map(Number);
  const first = new Date(y, mo - 1, 1);
  const daysInMonth = new Date(y, mo, 0).getDate();
  const cells = [];
  for (let i = 0; i < first.getDay(); i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(`${ym}-${String(d).padStart(2, '0')}`);
  while (cells.length % 7) cells.push(null);

  const idx = months.indexOf(ym);
  const shift = (delta) => {
    const d = new Date(y, mo - 1 + delta, 1);
    setYm(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const monthGames = games.filter(g => (g.game_date || '').startsWith(ym) && g.status !== 'cancelled').length;

  return (
    <div className="card" style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <button type="button" className="btn-ghost" onClick={() => shift(-1)} aria-label="Previous month"
                style={{ padding: '6px 10px' }}><ChevronLeft size={14} /></button>
        <div style={{ fontWeight: 800, fontSize: 16, minWidth: 150, textAlign: 'center' }}>{formatYearMonth(ym)}</div>
        <button type="button" className="btn-ghost" onClick={() => shift(1)} aria-label="Next month"
                style={{ padding: '6px 10px' }}><ChevronRight size={14} /></button>
        <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
          {monthGames} game{monthGames === 1 ? '' : 's'}
          {idx === -1 && months.length > 0 && ' · no games this month'}
        </span>
        {ym !== todayStr.slice(0, 7) && (
          <button type="button" className="btn-ghost" onClick={() => setYm(todayStr.slice(0, 7))}
                  style={{ fontSize: 12, padding: '6px 10px', marginLeft: 'auto' }}>Today</button>
        )}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(88px, 1fr))', gap: 4, minWidth: 640 }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', textAlign: 'center', padding: '2px 0' }}>{d}</div>
          ))}
          {cells.map((date, i) => {
            const list = date ? (byDate.get(date) || []) : [];
            const isToday = date === todayStr;
            return (
              <div key={date || `x${i}`} style={{
                minHeight: 92, borderRadius: 8, padding: 5,
                background: date ? 'var(--surface-2)' : 'transparent',
                border: isToday ? '1px solid var(--ice)' : '1px solid transparent',
              }}>
                {date && (
                  <div style={{ fontSize: 11, fontWeight: 700, color: isToday ? 'var(--ice)' : 'var(--text-2)', marginBottom: 4 }}>
                    {parseInt(date.slice(8, 10), 10)}
                  </div>
                )}
                {list.slice(0, 4).map(g => {
                  const meta = STATUS_META[g.status] || STATUS_META.upcoming;
                  const final = g.is_scoresheet_locked && g.result_home_score != null;
                  return (
                    <Link key={g.id} to={`/league/${leagueId}/game/${g.id}`} title={`${g.home_team_name} vs ${g.away_team_name}${g.venue_name ? ` · ${g.venue_name}` : ''}`}
                      style={{
                        display: 'block', fontSize: 10.5, lineHeight: 1.3, padding: '3px 5px', borderRadius: 6, marginBottom: 3,
                        textDecoration: g.status === 'cancelled' ? 'line-through' : 'none',
                        background: final ? 'var(--surface)' : meta.bg, color: 'var(--text)',
                        borderLeft: `3px solid ${final ? 'var(--goal)' : meta.color}`,
                        overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                      }}>
                      <span style={{ color: 'var(--text-2)' }}>{formatTime(g.game_time)}</span>{' '}
                      {shortName(g.home_team_name)} {final ? <strong>{g.result_home_score}–{g.result_away_score}</strong> : 'v'} {shortName(g.away_team_name)}
                    </Link>
                  );
                })}
                {list.length > 4 && (
                  <div style={{ fontSize: 10, color: 'var(--text-2)' }}>+{list.length - 4} more</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 8 }}>
        Click a game to open its game sheet. Final scores show once the scoresheet is signed and locked.
      </div>
    </div>
  );
}

function shortName(name) {
  const n = String(name || '').trim();
  if (n.length <= 14) return n;
  const words = n.split(/\s+/);
  return words.length > 1 ? words[words.length - 1] : n.slice(0, 13) + '…';
}

function ScheduleEmpty({ canManage, onGenerate }) {
  return (
    <div className="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
      <Calendar size={40} color="var(--text-2)" style={{ margin: '0 auto 14px', opacity: 0.5 }} />
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>No games scheduled yet</div>
      <div style={{ fontSize: 13, color: 'var(--text-2)', maxWidth: 460, margin: '0 auto 18px' }}>
        Run the schedule generator to lay out a round-robin season using your venues' weekly slots,
        avoiding blackout dates and double-bookings.
      </div>
      {canManage && (
        <button type="button" className="btn-ice" onClick={onGenerate}>
          <Sparkles size={14} style={{ marginRight: 8 }} />
          Generate schedule
        </button>
      )}
    </div>
  );
}

// ─── Game row + inline edit ───────────────────────────────────

const STATUS_META = {
  upcoming:    { label: 'Upcoming',     color: 'var(--ice)',     bg: 'var(--ice-a10)' },
  roster_open: { label: 'Roster open',  color: 'var(--goal)',    bg: 'var(--goal-a20)' },
  locked:      { label: 'Locked',       color: 'var(--puck)',    bg: 'var(--puck-a15)' },
  completed:   { label: 'Completed',    color: 'var(--text-2)',  bg: 'var(--surface-2)' },
  cancelled:   { label: 'Cancelled',    color: 'var(--penalty)', bg: 'var(--penalty-a15)' },
};

function GameRow({ game, isLast, leagueId, seasonId, venues, canManage, expanded, onToggle, onChanged }) {
  const meta = STATUS_META[game.status] || STATUS_META.upcoming;
  return (
    <div style={{
      borderBottom: isLast ? 'none' : '1px solid var(--border)',
      background: game.status === 'cancelled' ? 'var(--surface-2)' : 'transparent',
      opacity: game.status === 'cancelled' ? 0.65 : 1,
    }}>
      <button type="button" onClick={onToggle} style={{
        width: '100%', textAlign: 'left',
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '12px 16px',
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: 'var(--text)',
      }}>
        {/* Date pill */}
        <div style={{
          flexShrink: 0, width: 56, padding: '6px 4px',
          borderRadius: 8, background: 'var(--surface-2)',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase' }}>
            {formatDow(game.game_date)}
          </div>
          <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.1 }}>
            {parseInt((game.game_date || '').slice(8, 10), 10)}
          </div>
          <div style={{ fontSize: 9, color: 'var(--text-3)' }}>
            {formatMonShort(game.game_date)}
          </div>
        </div>
        {/* Matchup */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>
            {game.home_team_name || '(home)'} <span style={{ color: 'var(--text-3)' }}>vs</span> {game.away_team_name || '(away)'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Clock size={10} /> {formatTime(game.game_time)}
            </span>
            {game.venue_name && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <MapPin size={10} /> {game.venue_name}
              </span>
            )}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Shield size={10} />
              {game.officials_assigned}/4 officials
              {game.officials_signed > 0 && <> · {game.officials_signed} signed</>}
            </span>
          </div>
        </div>
        <span className="badge" style={{
          background: meta.bg, color: meta.color,
          fontSize: 10, padding: '2px 8px', whiteSpace: 'nowrap',
        }}>
          {meta.label}
        </span>
        {expanded ? <ChevronDown size={16} color="var(--text-2)" /> : <ChevronRight size={16} color="var(--text-2)" />}
      </button>
      {expanded && (
        <GameEditPanel game={game} leagueId={leagueId} seasonId={seasonId}
          venues={venues} canManage={canManage} onChanged={onChanged} />
      )}
    </div>
  );
}

function GameEditPanel({ game, leagueId, seasonId, venues, canManage, onChanged }) {
  const [date, setDate]   = useState(game.game_date || '');
  const [time, setTime]   = useState((game.game_time || '').slice(0, 5));
  const [venueId, setVenueId] = useState(game.venue_id || '');
  const [saving, setSaving]   = useState(false);

  async function save() {
    const body = {};
    if (date !== game.game_date) body.game_date = date;
    if (time !== (game.game_time || '').slice(0, 5)) body.game_time = time;
    if ((venueId || null) !== (game.venue_id || null)) body.venue_id = venueId || null;
    if (Object.keys(body).length === 0) return toast('Nothing to save');
    setSaving(true);
    try {
      await api.patch(`/leagues/${leagueId}/seasons/${seasonId}/games/${game.id}`, body);
      toast.success('Game updated');
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  }
  async function cancelGame() {
    if (!window.confirm('Cancel this game? It can\'t be un-cancelled from the UI.')) return;
    try {
      await api.post(`/leagues/${leagueId}/seasons/${seasonId}/games/${game.id}/cancel`);
      toast.success('Game cancelled');
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not cancel');
    }
  }

  return (
    <div style={{ padding: '12px 16px', background: 'var(--surface-2)', borderTop: '1px solid var(--border)' }}>
      {canManage && game.status !== 'cancelled' && !game.is_scoresheet_locked ? (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            <Field label="Date" style={{ flex: '1 1 140px' }}>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Time" style={{ flex: '1 1 120px' }}>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Venue" style={{ flex: '2 1 220px' }}>
              <select value={venueId} onChange={(e) => setVenueId(e.target.value)} style={inputStyle}>
                <option value="">(no venue)</option>
                {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" className="btn-ghost" onClick={cancelGame} disabled={saving}
              style={{ color: 'var(--penalty)', borderColor: 'var(--penalty-a30)', fontSize: 12, padding: '6px 12px' }}>
              <Trash2 size={12} style={{ marginRight: 4 }} />
              Cancel game
            </button>
            {/* flexWrap: three actions (Game sheet / Manage officials / Save)
                would overflow a ~390px phone width otherwise. */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Link to={`/league/${leagueId}/game/${game.id}`}
                className="btn-ghost" style={{ fontSize: 12, padding: '6px 12px' }}>
                <PenLine size={12} style={{ marginRight: 4 }} />
                Game sheet
              </Link>
              <Link to={`/league/${leagueId}/officials?game=${game.id}`}
                className="btn-ghost" style={{ fontSize: 12, padding: '6px 12px' }}>
                <Shield size={12} style={{ marginRight: 4 }} />
                Manage officials
              </Link>
              <button type="button" className="btn-ice" onClick={save} disabled={saving}
                style={{ fontSize: 12, padding: '6px 12px' }}>
                <Save size={12} style={{ marginRight: 4 }} />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
          {game.status === 'cancelled' && 'This game is cancelled.'}
          {game.is_scoresheet_locked && 'Scoresheet locked — game is final and cannot be edited.'}
          {!canManage && !game.is_scoresheet_locked && game.status !== 'cancelled' &&
            'You can see this game but need the manage_schedule permission to edit it.'}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Generator wizard (3 steps: constraints → preview → done)
// ═══════════════════════════════════════════════════════════════

function GeneratorWizard({ leagueId, seasonId, hasExistingGames, onClose, onDone }) {
  const [step, setStep] = useState(0);  // 0: constraints, 1: preview, 2: done
  const [format, setFormat] = useState('round_robin');
  const [gamesPerPair, setGamesPerPair] = useState(1);
  const [crossDivisionPairs, setCrossDivisionPairs] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(true);
  const [previewResult, setPreviewResult] = useState(null);  // {games, warnings, inputs_summary, constraints}
  const [generating, setGenerating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted] = useState(null);

  async function runPreview() {
    setGenerating(true);
    try {
      const { data } = await api.post(
        `/leagues/${leagueId}/seasons/${seasonId}/schedule/generate`,
        {
          format,
          games_per_pair: gamesPerPair,
          cross_division_pairs: crossDivisionPairs,
        }
      );
      setPreviewResult(data);
      setStep(1);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  }
  async function runCommit() {
    setCommitting(true);
    try {
      const { data } = await api.post(
        `/leagues/${leagueId}/seasons/${seasonId}/schedule/commit`,
        {
          games: previewResult.games,
          constraints: previewResult.constraints,
          replace_existing: hasExistingGames ? replaceExisting : false,
        }
      );
      setCommitted(data);
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Commit failed');
    } finally {
      setCommitting(false);
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      {/* v2.5.x — mobile: var(--mobile-h) tracks the real visible viewport
          (keyboard/chrome-aware) instead of a fixed 92vh that can clip on
          iOS Safari / Capacitor. */}
      <div className="card" style={{ maxWidth: 680, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.92)', overflowY: 'auto' }}>
        <ModalHeader title="Schedule generator" onClose={onClose} />
        <StepDots step={step} />

        {step === 0 && (
          <ConstraintsStep
            format={format} setFormat={setFormat}
            gamesPerPair={gamesPerPair} setGamesPerPair={setGamesPerPair}
            crossDivisionPairs={crossDivisionPairs} setCrossDivisionPairs={setCrossDivisionPairs}
            replaceExisting={replaceExisting} setReplaceExisting={setReplaceExisting}
            hasExistingGames={hasExistingGames}
            generating={generating}
            onCancel={onClose} onNext={runPreview}
          />
        )}
        {step === 1 && previewResult && (
          <PreviewStep
            result={previewResult}
            committing={committing}
            replaceExisting={replaceExisting}
            hasExistingGames={hasExistingGames}
            onBack={() => setStep(0)}
            onCommit={runCommit}
          />
        )}
        {step === 2 && committed && (
          <DoneStep summary={committed} onClose={onDone} />
        )}
      </div>
    </ModalBackdrop>
  );
}

function StepDots({ step }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 18 }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{
          width: 8, height: 8, borderRadius: '50%',
          background: i === step ? 'var(--ice)' : (i < step ? 'var(--ice-a40)' : 'var(--surface-2)'),
          border: i === step ? 'none' : '1px solid var(--border)',
        }} />
      ))}
    </div>
  );
}

function ConstraintsStep({
  format, setFormat, gamesPerPair, setGamesPerPair,
  crossDivisionPairs, setCrossDivisionPairs,
  replaceExisting, setReplaceExisting,
  hasExistingGames, generating, onCancel, onNext,
}) {
  return (
    <>
      <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 16 }}>
        Pick the matchup format and how many games each pair plays. The generator places games into
        venue slots, avoiding blackout dates and team double-bookings.
      </p>

      <Field label="Matchup format">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <RadioCard
            value="round_robin" current={format} onClick={setFormat}
            label="Round robin within division"
            description="Each team plays every OTHER team in their own division. Teams without a division play each other." />
          <RadioCard
            value="cross_division" current={format} onClick={setFormat}
            label="Full league round robin"
            description="Every team plays every other team in the league regardless of division." />
        </div>
      </Field>

      {format === 'round_robin' && (
        <Field label="Cross-division extras" hint="Add inter-division matchups on top of within-division play.">
          <CheckRow checked={crossDivisionPairs} onChange={setCrossDivisionPairs}
            label="Also schedule cross-division games" />
        </Field>
      )}

      <Field label="Games per pair" hint="How many times each pair plays — 1 for a single round robin, 2 for home-and-away.">
        <input type="number" value={gamesPerPair} min={1} max={10}
          onChange={(e) => setGamesPerPair(parseInt(e.target.value, 10) || 1)}
          style={{ ...inputStyle, width: 100 }} />
      </Field>

      {hasExistingGames && (
        <div style={{
          background: 'var(--puck-a15)', borderLeft: '3px solid var(--puck)',
          borderRadius: 8, padding: 12, marginBottom: 14,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--puck)', marginBottom: 6, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={14} />
            This season already has scheduled games
          </div>
          <CheckRow checked={replaceExisting} onChange={setReplaceExisting}
            label="Cancel all existing upcoming games before committing the new schedule"
            small />
          <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 6 }}>
            Cancelled games stay in the history. Locked / completed games are untouched either way.
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
        <button type="button" className="btn-ghost" onClick={onCancel} disabled={generating}>Cancel</button>
        <button type="button" className="btn-ice" onClick={onNext} disabled={generating}>
          {generating ? 'Generating…' : (
            <>
              Generate preview
              <ArrowRight size={13} style={{ marginLeft: 6 }} />
            </>
          )}
        </button>
      </div>
    </>
  );
}

function PreviewStep({ result, committing, replaceExisting, hasExistingGames, onBack, onCommit }) {
  const { games, warnings, inputs_summary } = result;
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 14 }}>
        <SummaryCard label="Games" value={games.length} color="var(--goal)" />
        <SummaryCard label="Teams" value={inputs_summary.team_count} color="var(--ice)" />
        <SummaryCard label="Divisions" value={inputs_summary.division_count} color="var(--ice)" />
        <SummaryCard label="Venues" value={inputs_summary.venue_count} color="var(--ice)" />
        <SummaryCard label="Slot candidates" value={inputs_summary.slot_candidate_count} color="var(--text-2)" />
        <SummaryCard label="Warnings" value={warnings.length} color={warnings.length > 0 ? 'var(--puck)' : 'var(--text-2)'} />
      </div>

      {warnings.length > 0 && (
        <div style={{
          background: 'var(--puck-a15)', borderLeft: '3px solid var(--puck)',
          borderRadius: 8, padding: 12, marginBottom: 14,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--puck)', marginBottom: 6 }}>
            {warnings.length} pairing{warnings.length === 1 ? '' : 's'} couldn't be placed
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-2)', maxHeight: 100, overflowY: 'auto' }}>
            {warnings.slice(0, 8).map((w, i) => (
              <div key={i} style={{ marginBottom: 2 }}>
                • {w.team_a_name} vs {w.team_b_name}
              </div>
            ))}
            {warnings.length > 8 && <div>…and {warnings.length - 8} more</div>}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 8 }}>
            Add more venue slots, extend the season, or reduce games per pair to fit these in.
          </div>
        </div>
      )}

      <details style={{ marginBottom: 14 }}>
        <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--text-2)' }}>
          Game-by-game preview ({games.length})
        </summary>
        <div style={{
          maxHeight: 280, overflowY: 'auto',
          marginTop: 8, border: '1px solid var(--border)', borderRadius: 8,
        }}>
          {games.map((g, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '6px 12px', borderBottom: '1px solid var(--border)',
              fontSize: 11,
            }}>
              <span style={{ color: 'var(--text-3)', fontFamily: 'var(--font-mono)', minWidth: 86 }}>
                {formatDate(g.game_date)} {(g.game_time || '').slice(0, 5)}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <strong>{g.home_team_name}</strong> vs <strong>{g.away_team_name}</strong>
              </span>
              <span style={{ color: 'var(--text-2)' }}>{g.venue_name}</span>
            </div>
          ))}
        </div>
      </details>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" className="btn-ghost" onClick={onBack} disabled={committing}>
          <ArrowLeft size={13} style={{ marginRight: 6 }} />
          Back
        </button>
        <button type="button" className="btn-ice" onClick={onCommit} disabled={committing || games.length === 0}>
          {committing ? 'Committing…' : (
            <>
              {hasExistingGames && replaceExisting ? 'Replace & commit' : 'Commit schedule'}
              {' '}({games.length})
            </>
          )}
        </button>
      </div>
    </>
  );
}

function DoneStep({ summary, onClose }) {
  return (
    <>
      <div style={{ textAlign: 'center', padding: '8px 0 18px' }}>
        <CheckCircle2 size={44} color="var(--goal)" style={{ margin: '0 auto 10px' }} />
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Schedule committed</div>
        <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
          Created <strong style={{ color: 'var(--text)' }}>{summary.inserted}</strong> game{summary.inserted === 1 ? '' : 's'}
          {summary.cancelled > 0 && <> · cancelled {summary.cancelled} prior game{summary.cancelled === 1 ? '' : 's'}</>}.
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="btn-ice" onClick={onClose}>Done</button>
      </div>
    </>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div style={{
      padding: 12, borderRadius: 10,
      background: 'var(--surface-2)', border: '1px solid var(--border)',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 }}>
        {label}
      </div>
    </div>
  );
}

function RadioCard({ value, current, onClick, label, description }) {
  const active = value === current;
  return (
    <button type="button" onClick={() => onClick(value)} style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: 12, borderRadius: 10,
      background: active ? 'var(--ice-a10)' : 'var(--surface-2)',
      border: `1px solid ${active ? 'var(--ice-a40)' : 'var(--border)'}`,
      cursor: 'pointer', textAlign: 'left', color: 'var(--text)',
    }}>
      <div style={{
        width: 16, height: 16, borderRadius: '50%',
        marginTop: 2, flexShrink: 0,
        background: active ? 'var(--ice)' : 'transparent',
        border: `1.5px solid ${active ? 'var(--ice)' : 'var(--text-2)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {active && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--btn-accent-text)' }} />}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>{description}</div>
      </div>
    </button>
  );
}

function CheckRow({ checked, onChange, label, small }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: small ? '4px 0' : 10, borderRadius: 8,
      background: 'transparent', border: 'none', cursor: 'pointer',
      color: 'var(--text)', textAlign: 'left',
    }}>
      <div style={{
        width: 16, height: 16, borderRadius: 4, flexShrink: 0,
        background: checked ? 'var(--ice)' : 'var(--surface)',
        border: `1px solid ${checked ? 'var(--ice)' : 'var(--border)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {checked && <CheckCircle2 size={11} color="var(--btn-accent-text)" strokeWidth={3} />}
      </div>
      <span style={{ fontSize: small ? 12 : 13, fontWeight: 500 }}>{label}</span>
    </button>
  );
}

// ─── Shared bits ──────────────────────────────────────────────

function ModalBackdrop({ onClose, children }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16, zIndex: 100,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        {children}
      </div>
    </div>
  );
}
function ModalHeader({ title, onClose }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
      <h2 className="section-title text-lg">{title}</h2>
      <button type="button" onClick={onClose}
        style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer' }}>
        <X size={18} />
      </button>
    </div>
  );
}
function Field({ label, hint, children, style }) {
  return (
    <label style={{ display: 'block', marginBottom: 12, ...style }}>
      <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
        {label}
      </span>
      {children}
      {hint && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>{hint}</span>}
    </label>
  );
}

function formatYearMonth(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  return date.toLocaleString(undefined, { month: 'long', year: 'numeric' });
}
function formatDow(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleString(undefined, { weekday: 'short' });
  } catch { return ''; }
}
function formatMonShort(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleString(undefined, { month: 'short' });
  } catch { return ''; }
}
function formatTime(t) {
  if (!t) return '';
  return t.slice(0, 5);
}

const inputStyle = {
  width: '100%',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '8px 12px',
  color: 'var(--text)',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  outline: 'none',
};
